import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AUTOFILL_PAGE_PATH, AUTOFILL_STORE_URL, isAutofillSupportedJob, isMobileDevice } from '../../utils/autofillExtension';
import './Autofill.css';

const ICON_SRC = '/images/autofill/store-icon-128.png';
const DASHBOARD_CARD_DISMISSED_KEY = 'seekjobs:autofill-card-dismissed';

export const MOBILE_INSTALL_TEXT = 'Open this page on your computer to install';

// The store icon, with a plain icon until the image asset is added to public/images/autofill.
export const AutofillIcon = ({ size = 40, className = '' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className={`af-icon af-icon--fallback ${className}`} style={{ width: size, height: size, fontSize: size * 0.45 }} aria-hidden="true">
        <i className="fa-solid fa-wand-magic-sparkles" />
      </span>
    );
  }
  return <img className={`af-icon ${className}`} src={ICON_SRC} alt="" width={size} height={size} onError={() => setFailed(true)} />;
};

// Chrome extensions install only on a computer, so phones get a note instead of the button.
export const AddToChromeButton = ({ className = 'af-btn af-btn--primary', label = 'Add to Chrome', mobileClassName = 'af-mobile-note' }) => {
  const [mobile] = useState(isMobileDevice);
  if (mobile) return <p className={mobileClassName}>{MOBILE_INSTALL_TEXT}</p>;
  return (
    <a className={className} href={AUTOFILL_STORE_URL} target="_blank" rel="noopener noreferrer">
      <i className="fa-brands fa-chrome" aria-hidden="true" /> {label}
    </a>
  );
};

// One-line pointer to the Autofill page. Opens in a new tab so it works inside the profile offcanvas.
export const AutofillNote = ({ children, className = '' }) => (
  <p className={`af-note ${className}`}>
    <AutofillIcon size={18} className="af-note__icon" />
    <span>
      {children}{' '}
      <a href={AUTOFILL_PAGE_PATH} target="_blank" rel="noopener noreferrer" className="af-link">
        Learn about SeekJobs Autofill
      </a>
    </span>
  </p>
);

const readDismissed = () => {
  try {
    return window.localStorage.getItem(DASHBOARD_CARD_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

export const AutofillDashboardCard = () => {
  const [dismissed, setDismissed] = useState(readDismissed);
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DASHBOARD_CARD_DISMISSED_KEY, '1');
    } catch {
      // Storage can be blocked; the card stays hidden for this visit.
    }
  };

  return (
    <section className="af-card" aria-labelledby="af-card-title">
      <button type="button" className="af-card__close" onClick={dismiss} aria-label="Dismiss SeekJobs Autofill">
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
      <div className="af-card__head">
        <AutofillIcon size={32} />
        <span className="af-badge">New</span>
      </div>
      <h2 id="af-card-title" className="af-card__title">Fill in job applications faster with SeekJobs Autofill</h2>
      <AddToChromeButton className="af-btn af-btn--primary af-btn--block" />
      <Link to={AUTOFILL_PAGE_PATH} className="af-link af-card__more">How it works</Link>
    </section>
  );
};

// Shown under Apply Now only for jobs on a site the extension supports.
export const AutofillApplyPrompt = ({ job, style }) => {
  const [mobile] = useState(isMobileDevice);
  if (mobile || !isAutofillSupportedJob(job)) return null;
  return (
    <p className="af-apply-prompt" style={style}>
      <AutofillIcon size={18} className="af-note__icon" />
      <span>
        Fill in this application page by page with SeekJobs Autofill.{' '}
        <a href={AUTOFILL_STORE_URL} target="_blank" rel="noopener noreferrer" className="af-link">
          Add to Chrome <span aria-hidden="true">→</span>
        </a>
      </span>
    </p>
  );
};
